import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import { DURATION, EASE_OUT, transitions } from "../../../components/motion";
import { cn } from "../../../utils/classnames";
import {
  DirectionsCarIcon,
  PersonRaisedHandIcon,
} from "../../../components/icons";
import { BookSection } from "../../../components/book-section/BookSection";
import { Chauffeur, CHAUFFEUR_SECTION_IDS } from "./components/Chauffeur";
import { MeetGreet } from "./components/MeetGreet";
import { WhyChooseTaxiSection } from "./components/WhyChooseTaxiSection";
import { scrollToSection } from "./navigation";
import {
  getImageFocusStyle,
  IMAGE_FOCUS_CLASS,
  SERVICE_IMAGES,
} from "./serviceImages";

const CHAUFFEUR_TAB = "Chauffeur";
const MEET_GREET_TAB = "MeetGreet";

const ACTIVE_TAB_COLOR = "#282828";
const INACTIVE_TAB_COLOR = "#8D8D8D";

const tabIconColor = (isActive) =>
  isActive ? ACTIVE_TAB_COLOR : INACTIVE_TAB_COLOR;

const TAB_LIST = [
  {
    id: CHAUFFEUR_TAB,
    icon: (isActive) => <DirectionsCarIcon color={tabIconColor(isActive)} />,
  },
  {
    id: MEET_GREET_TAB,
    icon: (isActive) => (
      <PersonRaisedHandIcon color={tabIconColor(isActive)} />
    ),
  },
];

/** How far the panels slide while they cross-fade, in pixels. */
const PANEL_SLIDE_PX = 28;

/** Where a tab sits in the strip, so we know which way the content moves. */
const indexOfTab = (tabId) => TAB_LIST.findIndex((tab) => tab.id === tabId);

/**
 * Remembers which tab was open before this one and reports the direction of
 * the move: 1 when the new tab is further right in the strip, -1 when it is
 * further left. The panels then slide the same way the eye just travelled.
 */
const useTabSlideDirection = (activeTab) => {
  const previousTabIndexRef = useRef(indexOfTab(activeTab));
  const direction =
    indexOfTab(activeTab) >= previousTabIndexRef.current ? 1 : -1;

  useEffect(() => {
    previousTabIndexRef.current = indexOfTab(activeTab);
  }, [activeTab]);

  return direction;
};

/**
 * The three services that used to be their own tabs are now sub-sections of
 * the Chauffeur tab, so older links such as `/services?type=PrivateTour` open
 * that tab and scroll to the matching section.
 */
const LEGACY_TYPE_TO_SECTION_ID = {
  AirportTransfer: CHAUFFEUR_SECTION_IDS.airportTransfer,
  ExecutiveTransfer: CHAUFFEUR_SECTION_IDS.businessTransfer,
  PrivateTour: CHAUFFEUR_SECTION_IDS.privateTour,
};

const ServicesPage = () => {
  const [activeTab, setActiveTab] = useState(CHAUFFEUR_TAB);
  const { t } = useTranslation();
  const { location, push } = useHistory();

  const slideDirection = useTabSlideDirection(activeTab);
  const shouldReduceMotion = useReducedMotion();

  // With prefers-reduced-motion the panels only fade: no sliding, no bounce.
  const panelSlide = shouldReduceMotion ? 0 : PANEL_SLIDE_PX;
  const panelTransition = shouldReduceMotion
    ? { duration: DURATION.fast }
    : { duration: DURATION.base, ease: EASE_OUT };

  useEffect(() => {
    const type = new URLSearchParams(location.search).get("type");
    if (!type) return;

    if (TAB_LIST.some((tab) => tab.id === type)) {
      setActiveTab(type);
      return;
    }

    const legacySectionId = LEGACY_TYPE_TO_SECTION_ID[type];
    if (legacySectionId) {
      setActiveTab(CHAUFFEUR_TAB);
      scrollToSection(legacySectionId);
    }
  }, [location.search]);

  return (
    <FrontLayout>
      <div className="tw:relative tw:max-w-[1920px] tw:!mx-auto">
        <div className="tw:max-w-[1920px] tw:m-auto tw:relative">
          <div className="tw:bg-[linear-gradient(87.06deg,#000000_6.43%,rgba(40,42,45,0.3)_46.3%)] tw:w-full tw:h-full tw:absolute tw:top-0 tw:left-0"></div>
          <img
            src={SERVICE_IMAGES.pageBanner.src}
            alt={t("servicesPage.Eyebrow")}
            style={getImageFocusStyle(SERVICE_IMAGES.pageBanner)}
            className={cn(
              "tw:w-full tw:h-[300px] tw:lg:h-[430px] tw:object-cover",
              IMAGE_FOCUS_CLASS
            )}
          />
          <div className="tw:absolute tw:left-5 tw:bottom-8 tw:lg:left-[82px] tw:lg:bottom-[65px] tw:space-y-[14px] tw:z-50">
            <div className="tw:text-[rgba(219,190,120,1)] tw:text-[8px] tw:tracking-[3px] tw:font-semibold tw:font-hanken-grotesk tw:leading-none tw:uppercase">
              {t("servicesPage.Eyebrow")}
            </div>
            {/* The title changes with the tab, so swap it instead of
                letting it flick from one word to the other. */}
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeTab}
                className="tw:text-[34px] tw:lg:text-[45px] tw:font-libre-caslon-display tw:leading-[48px] tw:lg:leading-[58px] tw:text-white tw:font-normal"
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -10 }}
                transition={{ duration: DURATION.fast, ease: EASE_OUT }}
              >
                {t(`servicesPage.Tabs.${activeTab}.Label`)}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Tab bar styled like folder tabs: the active tab is a white card
            with no bottom edge, so it looks joined to the white content
            below while the beige strip and its bottom rule sit behind it. */}
        <div className="tw:bg-[#F6F4F0] tw:border-b tw:border-[#E7E2D8] tw:px-3 tw:lg:px-[82px] tw:pt-3 tw:lg:pt-5">
          <div className="tw:flex tw:-mb-px">
            {TAB_LIST.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  type="button"
                  key={tab.id}
                  onClick={() => push(`?type=${tab.id}`)}
                  aria-pressed={isActive}
                  className={cn(
                    "tw:relative tw:flex-1 tw:h-[56px] tw:lg:h-[68px] tw:px-3 tw:lg:px-[26px] tw:flex tw:items-center tw:justify-center tw:bg-transparent tw:border-0 tw:font-hanken-grotesk tw:font-medium tw:leading-none tw:text-[14px] tw:lg:text-[16px] tw:transition-colors",
                    isActive
                      ? "tw:text-[#282828]"
                      : "tw:text-[#8D8D8D] tw:cursor-pointer tw:hover:text-[#4E4E4E]"
                  )}
                >
                  {/* One white card shared by both tabs: giving it the same
                      `layoutId` makes framer glide it across instead of
                      redrawing it under the tab that was clicked. */}
                  {isActive && (
                    <motion.span
                      layoutId="servicesActiveTabCard"
                      className="tw:absolute tw:inset-0 tw:bg-white tw:border tw:border-b-0 tw:border-[#8D8D8D] tw:rounded-t-[5px]"
                      transition={
                        shouldReduceMotion
                          ? { duration: 0 }
                          : transitions.spring
                      }
                    />
                  )}
                  <span className="tw:relative tw:flex tw:items-center tw:justify-center tw:gap-x-2 tw:lg:gap-x-3">
                    {tab.icon(isActive)}
                    <span>{t(`servicesPage.Tabs.${tab.id}.Label`)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Only one panel exists at a time: `mode="wait"` lets the old one
            fade out before the new one arrives, which keeps the page from
            jumping while two very tall panels overlap. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: slideDirection * panelSlide }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: slideDirection * -panelSlide }}
            transition={panelTransition}
          >
            {activeTab === CHAUFFEUR_TAB ? <Chauffeur /> : <MeetGreet />}
          </motion.div>
        </AnimatePresence>
        <WhyChooseTaxiSection />
        <BookSection />
      </div>
    </FrontLayout>
  );
};

export default ServicesPage;
